import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './ChatWidget.css';
import logoBot from '../logo_bullminer.png'; // Asegúrate que la ruta sea correcta
import api from '../api'; // En lugar de axios


const ChatWidget = ({ lessonId, lessonTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estado para PDF
  const [studentContext, setStudentContext] = useState(""); 
  const [fileName, setFileName] = useState("");
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- EFECTO: REINICIAR CHAT AL CAMBIAR DE LECCIÓN ---
  useEffect(() => {
    setMessages([
      { sender: 'bot', text: `Hola, soy el asistente de The Bull Miner. ¿En qué te ayudo con la clase "${lessonTitle}"?` }
    ]);
    setStudentContext("");
    setFileName("");
  }, [lessonId, lessonTitle]); // Se ejecuta cuando cambia la lección

  // Scroll automático al fondo
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  // 1. SUBIR PDF
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post('/chat/analyze-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setStudentContext(res.data.extracted_text);
      setFileName(res.data.filename);
      setMessages(prev => [...prev, { sender: 'bot', text: `✅ He leído el archivo "${res.data.filename}". Pregúntame sobre él.` }]);
    } catch (error) {
      console.error(error);
      setMessages(prev => [...prev, { sender: 'bot', text: "Error al leer el PDF. Intenta con otro archivo." }]);
    } finally {
      setLoading(false);
      // Limpiar input para permitir subir el mismo archivo si falla
      e.target.value = null; 
    }
  };

  // 2. ENVIAR MENSAJE
  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = input;
    setMessages(prev => [...prev, { sender: 'user', text: userMsg }]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/chat/ask', {
        question: userMsg,
        lesson_id: lessonId,
        student_context: studentContext
      });
      
      setMessages(prev => [...prev, { sender: 'bot', text: response.data.answer }]);
    } catch (error) {
      setMessages(prev => [...prev, { sender: 'bot', text: "Error de conexión. Verifica que el servidor esté activo." }]);
    } finally {
      setLoading(false);
    }
  };

return (
    <div className={`chat-widget-wrapper ${isOpen ? 'open' : ''}`}>
      
      {/* BOTÓN FLOTANTE (TOGGLE) */}
      <button className="chat-toggle-btn" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? (
          <span className="close-icon">✕</span> // X Blanca
        ) : (
          <img src={logoBot} alt="AI" className="chat-logo-img" /> // Logo Dorado
        )}
      </button>

      {/* VENTANA DE CHAT */}
      {isOpen && (
        <div className="chat-window">
          
          {/* HEADER */}
          <div className="chat-header">
            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
               <img src={logoBot} alt="Logo" style={{width:'25px'}} />
               <h4 style={{margin:0, color:'#E6EDF3', fontSize:'1rem'}}>Bull Miner AI</h4>
            </div>
            <span className="ai-badge">BETA</span>
          </div>
          
          {/* MENSAJES */}
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.sender}`}>
                <div className="bubble">{msg.text}</div>
              </div>
            ))}
            {loading && <div className="message bot"><div className="bubble typing">...</div></div>}
            <div ref={messagesEndRef} />
          </div>

          {/* BARRA DE ARCHIVO ADJUNTO (PREVIEW) */}
          {fileName && (
            <div className="file-preview-bar">
              <div style={{display:'flex', alignItems:'center', gap:'5px', overflow:'hidden'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span style={{whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden', maxWidth:'200px'}}>{fileName}</span>
              </div>
              <button onClick={() => {setStudentContext(""); setFileName("");}} className="btn-remove-file">✕</button>
            </div>
          )}

          {/* INPUT AREA */}
          <form className="chat-input-area" onSubmit={handleSend}>
            
            {/* BOTÓN CLIP (SVG BLANCO) */}
            <button 
              type="button" 
              className="btn-attach" 
              onClick={() => fileInputRef.current.click()}
              title="Adjuntar PDF"
              disabled={loading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
              </svg>
            </button>
            
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{display:'none'}} 
              accept=".pdf"
              onChange={handleFileUpload}
            />

            <input 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)} 
              placeholder="Escribe tu pregunta..." 
              disabled={loading}
            />
            
            {/* BOTÓN ENVIAR (SVG FLECHA VERDE) */}
            <button type="submit" disabled={loading} className="btn-send">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2EA043" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                 <line x1="22" y1="2" x2="11" y2="13"></line>
                 <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
               </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;